import gql from 'graphql-tag';

const terminalOrderFragment = gql`
  fragment terminalOrder on AdyenTerminalOrder {
    adyenOrderReference
    courier
    id
    showDeliveryProgress
    source
    status
    terminalModel
    trackingCode
  }
`;

const terminalFragment = gql`
  fragment terminal on PosTerminal {
    description
    id
    name
    status
    model
    location {
      id
      internalName
    }
    terminalOrder {
      ...terminalOrder
    }
  }
  ${terminalOrderFragment}

`;

const dashboardDataFragment = gql`
  fragment dashboardData on DashboardData {
    terminals {
      ...terminal
    }
    hasPosTransaction
    lastTerminalOrderAt
    tips
    terminalOrders {
      ...terminalOrder
    }
  }
  ${terminalOrderFragment}
  ${terminalFragment}
`;

export const GET_ADYEN_DASHBOARD_DATA = gql`
  query getAdyenDashboardData {
    adyenDashboardData {
      availableBalance
      processingBalance
      ...dashboardData
    }
  }
  ${dashboardDataFragment}
`;

export const GET_ADYEN_DASHBOARD_DATA_WITHOUT_BALANCE = gql`
  query getAdyenDashboardData {
    adyenDashboardData {
      ...dashboardData
    }
  }
  ${dashboardDataFragment}
`;

export const GET_ADYEN_TIPS_DATA = gql`
  query getAdyenTipsData($startDate: ISO8601Date, $endDate: ISO8601Date) {
    adyenDashboardData(startDate: $startDate, endDate: $endDate) {
      tips
    }
  }
`;

export const GET_ADYEN_PAYOUTS = gql`
  query getAdyenPayouts(
    $pagination: PaginationAttributes!
    $balanceAccountId: Int
  ) {
    adyenPayouts(pagination: $pagination, balanceAccountId: $balanceAccountId) {
      id
      amountValue
      amountCurrency
      status
      reason
      reference
      transferredAt
      balanceAccount {
        id
        label
      }
      adyenTransferInstrument {
        id
        accountNumberLast4
      }
    }
  }
`;

export const UPDATE_ADYEN_APP_STATE = gql`
  mutation updateAdyenAppState($input: UpdateAdyenAppStateInput!) {
    updateAdyenAppState(input: $input) {
      adyenAppState {
        terminalTippingEnabled
      }
    }
  }
`;

export const GET_ADYEN_TERMINAL_SETTINGS = gql`
  query getAdyenTerminalSettings {
    adyenAppState {
      adyenStores {
        id
        locationId
        online
        predefinedTips
        terminalLanguage
      }
    }
  }
`;

export const UPDATE_POS_TERMINAL = gql`
  mutation updatePosTerminal($input: UpdatePosTerminalInput!) {
    updatePosTerminal(input: $input) {
      posTerminal {
        id
        name
      }
      errors
    }
  }
`;

export const UPDATE_TERMINAL_SETTINGS = gql`
  mutation updateTerminalSettings($input: UpdateTerminalSettingsInput!) {
    updateTerminalSettings(input: $input) {
      adyenAppState {
        adyenStores {
          id
          locationId
          online
          terminalLanguage
        }
      }
      errors {
        message
      }
    }
  }
`;

export const GET_ADYEN_BUSINESS_DETAILS = gql`
  query getAdyenBusinessDetails {
    adyenAppState {
      id
      verificationStatus
      legalEntityType
      transferInstruments {
        id
        accountNumberLast4
      }
    }
  }
`;

export const GET_ADYEN_WALLETS = gql`
  query getAdyenWallets {
    adyenAppState {
      verificationStatus
      balanceAccounts {
        id
        label
        isDefault
        currency
        status
        paymentRule {
          id
          basisPoints
          employee {
            id
            name
          }
        }
        adyenSweep {
          scheduleType
          adyenTransferInstrument {
            id
            accountNumberLast4
            bankName
            verification {
              status
            }
          }
        }
      }
    }
  }
`;

export const GET_ADYEN_WALLET_BALANCE = gql`
  query getAdyenWalletBalance($balanceAccountId: ID!) {
    adyenDashboardData(balanceAccountId: $balanceAccountId) {
      availableBalance
      processingBalance
    }
  }
`;

export const GET_ADYEN_WALLET_REPORT = gql`
  query getAdyenWalletReport($startDate: ISO8601Date!, $endDate: ISO8601Date!) {
    adyenWalletReport(startDate: $startDate, endDate: $endDate) {
      rows {
        balanceAccount {
          id
          label
          isDefault
          status
        }
        gross
        fees
        net
        shareBasisPoints
      }
    }
  }
`;

export const CREATE_ADYEN_BALANCE_ACCOUNT = gql`
  mutation createAdyenBalanceAccount($input: CreateAdyenBalanceAccountInput!) {
    createAdyenBalanceAccount(input: $input) {
      adyenBalanceAccount {
        id
      }
      errors {
        attribute
        message
        type
      }
    }
  }
`;

export const CLOSE_ADYEN_BALANCE_ACCOUNT = gql`
  mutation closeAdyenBalanceAccount($input: CloseAdyenBalanceAccountInput!) {
    closeAdyenBalanceAccount(input: $input) {
      adyenBalanceAccount {
        id
        status
      }
      errors {
        attribute
        message
        type
      }
    }
  }
`;

export const UPDATE_ADYEN_BALANCE_ACCOUNT = gql`
  mutation updateAdyenBalanceAccount($input: UpdateAdyenBalanceAccountInput!) {
    updateAdyenBalanceAccount(input: $input) {
      adyenBalanceAccount {
        id
      }
      errors {
        attribute
        message
        type
      }
    }
  }
`;

export const CREATE_ADYEN_ONBOARDING_COMPONENT_SESSION = gql`
  mutation createAdyenOnboardingComponentSession(
    $input: CreateAdyenOnboardingComponentSessionInput!
  ) {
    createAdyenOnboardingComponentSession(input: $input) {
      session {
        token
        environment
        legalEntityId
      }
      errors {
        message
      }
    }
  }
`;

export const SYNC_ADYEN_LEGAL_ENTITY = gql`
  mutation syncAdyenLegalEntity($input: SyncAdyenLegalEntityInput!) {
    syncAdyenLegalEntity(input: $input) {
      adyenAppState {
        id
        verificationStatus
        transferInstruments {
          id
          accountNumberLast4
        }
      }
    }
  }
`;
